import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import CopilotDemo from "@/components/od/demo/CopilotDemo";
import { MaskedLine } from "@/components/od/Hero";
import { BOOKS_COPILOT } from "@/data/demoData";

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to ask your books a question and get an instant answer, with Books Copilot",
  description:
    "Ask a plain question about your financials; the copilot reads your books and answers with the actual figures — what's billable, what's outstanding, cash position, profitability.",
  step: [
    { "@type": "HowToStep", position: 1, name: "Pick your seat", text: "Accounting or owner — each role asks its own question." },
    { "@type": "HowToStep", position: 2, name: "Ask the question", text: "The copilot reads live invoices, payments, and project costs from your books." },
    { "@type": "HowToStep", position: 3, name: "Act on the answer", text: "Draft invoices, reminders, and call lists arrive prepared for your approval." },
  ],
};

export default function BooksDemoPage() {
  useEffect(() => {
    document.title = "Books Copilot Demo — Ask Your Books a Question | Ocean Data";
  }, []);

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]">
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <Link
            to="/demos"
            data-testid="books-demo-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal mb-10 hover:text-brand-navy transition-colors"
          >
            <ArrowLeft size={15} /> All demos
          </Link>
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Live demo · your books · sample data
          </p>
          <h1
            data-testid="books-demo-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-3xl sm:text-4xl lg:text-5xl mb-6"
          >
            <MaskedLine delay={0.15} testId="books-demo-headline-line-1">Ask your books a question.</MaskedLine>
            <MaskedLine delay={0.3} testId="books-demo-headline-line-2">
              <span className="text-brand-teal">Get the answer while it's useful.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed mb-12"
          >
            What's billable today, what's outstanding, where cash stands, which
            projects are truly profitable — plain questions, precise answers,
            straight from the record.
          </motion.p>

          <CopilotDemo copilot={BOOKS_COPILOT} />

          <p className="mt-10 text-sm text-slate-500 leading-relaxed max-w-2xl">
            Under the hood, the copilot is connected directly to Zoho Books —
            invoices, payments, expenses, and project costs. No exports, no
            spreadsheet archaeology, no waiting for month-end.{" "}
            <Link to="/solutions" className="font-semibold text-brand-teal hover:text-brand-navy transition-colors">
              How the FIX service works →
            </Link>
          </p>
        </div>
      </section>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
    </main>
  );
}
